/**
 * Rendering a report through the framework's report route.
 *
 * The route is a CMS code module owned by the Appframe framework
 * (`WebsiteCMS.Reports`), registered on
 * `api/report/{ReportID}/{FileName}.{Extension}`. Everything here builds
 * requests for it — there is no report endpoint of our own.
 */

import { downloadRequest, type DownloadItem } from "../downloadRequest.js";
import type { ReportFormat, ReportParameterValues } from "./types.js";

/**
 * The file extension each format is requested with. The route only accepts
 * `(pdf|csv|xlsx?|html|xbm|mht|rtf|txt)`, which is why `TEXT` is `txt` and
 * `IMAGE` is `xbm` rather than the format name.
 */
const REPORT_EXTENSIONS: Record<ReportFormat, string> = {
	CSV: "csv",
	HTML: "html",
	IMAGE: "xbm",
	MHT: "mht",
	PDF: "pdf",
	RTF: "rtf",
	TEXT: "txt",
	XLS: "xls",
	XLSX: "xlsx",
};

/** The media type each format is served as, for previewing a rendered report. */
const REPORT_MEDIA_TYPES: Record<ReportFormat, string> = {
	CSV: "text/csv",
	HTML: "text/html",
	IMAGE: "image/x-xbitmap",
	MHT: "message/rfc822",
	PDF: "application/pdf",
	RTF: "text/enriched",
	TEXT: "text/plain",
	XLS: "application/vnd.ms-excel",
	XLSX: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

export type ReportRenderOptions = {
	reportId: string;
	format: ReportFormat;
	/** File name without an extension. Defaults to the report ID. */
	fileName?: string;
	/** The filter string the report is run with. */
	filter?: string | null;
	/** Parameter values by name. Only non-empty values are sent. */
	parameters?: ReportParameterValues | null;
	/** Text for the report's optional header band. */
	headerText?: string | null;
	/** Hides the filter string printed on the report. */
	hideFilterString?: boolean;
	/** Runs the report in another domain than the current one. */
	domain?: string | null;
	/** Timezone offset in hours or minutes. Defaults to the browser's. */
	timezone?: number | null;
	signal?: AbortSignal;
};

/** The extension a format is requested with. */
export function getReportExtension(format: ReportFormat): string {
	return REPORT_EXTENSIONS[format];
}

/** The media type a format is served as. */
export function getReportMediaType(format: ReportFormat): string {
	return REPORT_MEDIA_TYPES[format];
}

/**
 * Characters that must not reach the file name, because it is a path segment of
 * the route: separators, the extension delimiter, the query and fragment
 * delimiters, and control characters.
 */
// oxlint-disable-next-line no-control-regex -- control characters are the point
const UNSAFE_FILE_NAME_CHARACTERS = /[\\/:*?"<>|.#&+%\u0000-\u001f]/g;

/**
 * The name a rendered report is downloaded as, including its extension.
 *
 * The file name is part of the route rather than a query parameter, so anything
 * that would change the path is replaced.
 */
export function getReportFileName(options: Pick<ReportRenderOptions, "reportId" | "format" | "fileName">): string {
	let name = (options.fileName ?? options.reportId).trim();
	let safe = name.replace(UNSAFE_FILE_NAME_CHARACTERS, "-").replace(/-{2,}/g, "-").replace(/^-|-$/g, "").trim();

	return `${safe === "" ? options.reportId : safe}.${getReportExtension(options.format)}`;
}

/**
 * The route is always on the same origin, but a `Request` needs an absolute
 * URL, so the path is resolved against the document when there is one.
 */
function toRequestUrl(path: string): string {
	let base = globalThis.location?.href;

	return base ? new URL(path, base).toString() : path;
}

/**
 * The request that renders a report.
 *
 * A report without parameters is a plain `GET`, so it can be opened directly in
 * a new tab. As soon as there are parameters or a header the options go in a
 * JSON body, which the route only reads from a `POST`.
 *
 * `filter` is always sent alongside `parameters`: the route reads the filter out
 * of the body *guarded by the presence of `parameters`*, and reads it with an
 * indexer that throws when the key is missing.
 */
export function getReportRequest(options: ReportRenderOptions): Request {
	let { domain, filter, headerText, hideFilterString, parameters, reportId, signal, timezone } = options;
	let fileName = getReportFileName(options);
	let url = `/api/report/${encodeURIComponent(reportId)}/${encodeURIComponent(fileName)}`;
	let query = new URLSearchParams();
	let entries = toParameterEntries(parameters);
	let needsBody = entries.length > 0 || Boolean(headerText) || hideFilterString === true;

	if (domain) query.set("Domain", domain);
	query.set("timezone", String(timezone ?? -new Date().getTimezoneOffset() / 60));

	if (!needsBody) {
		if (filter) query.set("Filter", filter);

		return new Request(toRequestUrl(`${url}?${query}`), { credentials: "same-origin", signal });
	}

	let body: Record<string, unknown> = {
		filter: filter ?? "",
		parameters: entries,
	};

	if (headerText) body.optionalheader = headerText;
	// The route assigns `showfiltercriteria` straight to the report's
	// `HideFilterString`, so despite its name it *hides* the filter string.
	if (hideFilterString === true) body.showfiltercriteria = "true";

	return new Request(toRequestUrl(`${url}?${query}`), {
		body: JSON.stringify(body),
		credentials: "same-origin",
		headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
		method: "POST",
		signal,
	});
}

/**
 * Renders a report and resolves with the rendered file.
 *
 * The route answers with a JSON body carrying an `error` when rendering fails,
 * rather than with a failing status, so a JSON response is turned into an error
 * here.
 */
export async function renderReport(options: ReportRenderOptions): Promise<Blob> {
	let response = await fetch(getReportRequest(options));
	let contentType = response.headers.get("content-type") ?? "";

	if (contentType.includes("application/json")) {
		let data = (await response.json()) as { error?: string };

		throw new Error(data.error ?? "Failed to render report");
	}

	if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

	return response.blob();
}

/** Renders a report and saves it, reporting progress as it downloads. */
export function downloadReport(
	options: ReportRenderOptions & { onProgress?: (progress: DownloadItem) => void },
): Promise<void> {
	return downloadRequest(getReportRequest(options), {
		fileName: getReportFileName(options),
		onProgress: options.onProgress,
		signal: options.signal,
	});
}

/**
 * Renders a report and opens it in a new tab.
 *
 * The blob is rendered first rather than pointing a tab at the route, so a
 * failure surfaces as an error here instead of as a tab showing JSON, and so
 * reports with parameters — which have to be posted — can be previewed too.
 */
export async function openReport(options: ReportRenderOptions): Promise<void> {
	let blob = await renderReport(options);
	// The route serves some formats as types a browser downloads rather than
	// displays; the format's own media type is what decides how it opens.
	let url = URL.createObjectURL(blob.type ? blob : new Blob([blob], { type: getReportMediaType(options.format) }));
	let opened = globalThis.open(url, "_blank", "noopener");

	if (!opened) {
		URL.revokeObjectURL(url);
		throw new Error("The report could not be opened. Allow pop-ups for this site and try again.");
	}

	// Revoking immediately would cancel the load in some browsers, and never
	// revoking keeps the whole rendered report in memory.
	globalThis.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Renders a report and opens the browser's print dialog for it.
 *
 * The report is loaded into an offscreen iframe rather than a new tab, so the
 * print dialog opens over the launcher and the user is not left with a stray
 * tab afterwards. Browsers that will not print an embedded document fall back
 * to opening it, which leaves the user one keystroke from printing.
 */
export async function printReport(options: ReportRenderOptions): Promise<void> {
	let document_ = globalThis.document;
	if (!document_?.body) return openReport(options);

	let blob = await renderReport(options);
	let url = URL.createObjectURL(blob.type ? blob : new Blob([blob], { type: getReportMediaType(options.format) }));
	let frame = document_.createElement("iframe");
	// Hidden with `visibility` rather than `display: none`: a frame that is not
	// laid out has nothing to print in some browsers.
	frame.setAttribute("aria-hidden", "true");
	frame.style.cssText =
		"position:fixed;inset-block-start:0;inset-inline-start:0;inline-size:1px;block-size:1px;border:0;visibility:hidden";

	let cleanUp = () => {
		frame.remove();
		URL.revokeObjectURL(url);
	};

	try {
		await new Promise<void>((resolve, reject) => {
			frame.addEventListener("load", () => resolve(), { once: true });
			frame.addEventListener("error", () => reject(new Error("The report could not be loaded for printing")), {
				once: true,
			});
			frame.src = url;
			document_.body.append(frame);
		});

		frame.contentWindow?.focus();
		frame.contentWindow?.print();
	} catch {
		cleanUp();

		return openReport(options);
	}

	// The print dialog is modal but not awaitable, and revoking the URL or
	// removing the frame while it is open cancels the job.
	globalThis.setTimeout(cleanUp, 60_000);
}

/**
 * Parameters in the shape the route expects — `{ name, value }` objects.
 *
 * Empty values are dropped: the route skips them anyway, and a report falls back
 * to its own default when a parameter is not sent.
 */
function toParameterEntries(parameters: ReportParameterValues | null | undefined) {
	if (!parameters) return [];

	return Object.entries(parameters)
		.filter(([, value]) => value !== null && value !== undefined && value !== "")
		.map(([name, value]) => ({ name, value: typeof value === "boolean" ? String(value) : value }));
}
